import {
    Box,
    Button,
    Input,
    Text,
    VStack,
    Text as ChakraText
} from '@chakra-ui/react';
import * as React from 'react';
import Papa from 'papaparse';

interface CSVRow {
    fileId: string;
    tags?: string;
    [key: string]: string | undefined;
}

interface MetadataField {
    name: string;
    schema: {
        type: string;
    };
}

export const BulkUpdateCSV = () => {
    const [csvFile, setCsvFile] = React.useState<File | null>(null);
    const [status, setStatus] = React.useState('');
    const [failedRows, setFailedRows] = React.useState<Record<string, string>[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    
    const mounted = React.useRef(true);

    React.useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);

    const safeSetStatus = (message: string) => {
        if (mounted.current) {
            setStatus(message);
        }
    };

    const fetchMetadataFields = async (): Promise<MetadataField[]> => {
        const response = await fetch('/api/v1/customMetadataFields', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch metadata fields');
        }

        return response.json();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        setCsvFile(file);
    };

    //function to update file details one row at a time on client side
    const updateFile = async (row: CSVRow, metadataSchema: Map<string, string>) => {
        const payload: {
            tags?: string[];
            customMetadata?: Record<string, string | boolean | string[] | number>;
        } = {};

        // Handle tags
        if (row.tags) {
            payload.tags = row.tags.split(';').map(tag => tag.trim()).filter(tag => tag.length > 0);
        }

        // Handle metadata fields
        const metadata: Record<string, string | boolean | string[] | number> = {};
        Object.entries(row).forEach(([key, value]) => {
            if (key.startsWith('custom_metadata_') && value) {
                const metadataKey = key.replace('custom_metadata_', '');
                const schemaType = metadataSchema.get(metadataKey);
                
                if (!schemaType) {
                    console.warn(`Unknown metadata field: ${metadataKey}`);
                    return;
                }

                const trimmedValue = value.trim();
                switch (schemaType) {
                    case 'Boolean':
                        const lowerValue = trimmedValue.toLowerCase();
                        if (lowerValue === 'true' || lowerValue === 'false') {
                            metadata[metadataKey] = lowerValue === 'true';
                        }
                        break;
                    case 'Number':
                        if (!isNaN(Number(trimmedValue)) && trimmedValue !== '') {
                            metadata[metadataKey] = Number(trimmedValue);
                        }
                        break;
                    case 'MultiSelect':
                        metadata[metadataKey] = trimmedValue.split(';')
                            .map(item => item.trim())
                            .filter(item => item.length > 0);
                        break;
                    default:
                        metadata[metadataKey] = trimmedValue;
                }
            }
        });

        if (Object.keys(metadata).length > 0) {
            payload.customMetadata = metadata;
        }

        // Skip PATCH if no changes to make
        if (Object.keys(payload).length === 0) {
            return;
        }

        // Update file details API call
        const response = await fetch(`/api/v2/files/${row.fileId}/details`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`Failed to update file ${row.fileId}: ${response.statusText}`);
        }

        return response.json();
    };

    const downloadFailedRows = () => {
        if (failedRows.length === 0) return;
        
        // Get all unique headers from the failed rows
        const headers = Array.from(
            new Set(
                failedRows.flatMap(row => Object.keys(row))
            )
        );

        // Create CSV content
        const csvContent = [
            headers.join(','),
            ...failedRows.map(row => 
                headers.map(header => {
                    const value = row[header] || '';
                    // Escape values containing commas or quotes
                    return value.includes(',') || value.includes('"') 
                        ? `"${value.replace(/"/g, '""')}"` 
                        : value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `failed_updates_${Math.floor(Date.now() / 1000)}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    //function to start parsing the CSV file and start the bulk update process
    const handleParseCSV = () => {
        // Reset failed rows when starting new upload
        setFailedRows([]);
        
        if (!csvFile) {
            safeSetStatus('Please upload a CSV file');
            return;
        }

        if (!csvFile.name.toLowerCase().endsWith('.csv')) {
            safeSetStatus('Please upload a valid CSV file');
            return;
        }

        Papa.parse<Record<string, string>>(csvFile, {
            header: true,
            skipEmptyLines: true,
            transformHeader: header => header.trim(),
            complete: async (results) => {
                if (results.data.length === 0) {
                    safeSetStatus('CSV file is empty');
                    return;
                }

                const firstRow = results.data[0] as Record<string, string>;
                if (!('fileId' in firstRow)) {
                    safeSetStatus('CSV must contain a "fileId" column');
                    return;
                }

                try {
                    // Parse CSV rows as array of objects
                    const parsedRows: CSVRow[] = results.data
                        .filter(row => {
                            const typedRow = row as Record<string, string>;
                            return typedRow.fileId?.trim();
                        })
                        .map(row => {
                            const typedRow = row as Record<string, string>;
                            const rowObject: CSVRow = {
                                fileId: typedRow.fileId
                            };

                            Object.entries(typedRow).forEach(([key, value]) => {
                                if ((key === 'tags' || key.startsWith('custom_metadata_')) && value?.trim()) {
                                    rowObject[key] = value.trim();
                                }
                            });

                            return rowObject;
                        });

                    if (parsedRows.length === 0) {
                        safeSetStatus('No valid rows found in CSV');
                        return;
                    }

                    // Fetch metadata schema once before processing rows
                    const metadataFields = await fetchMetadataFields();
                    const metadataSchema = new Map<string, string>(
                        metadataFields.map(field => [field.name, field.schema.type])
                    );

                    // Start update of file details
                    safeSetStatus(`Starting update for ${parsedRows.length} files...`);
                    let successCount = 0;
                    let failureCount = 0;
                    const failedRowsData: Record<string, string>[] = [];

                    for (const row of parsedRows) {
                        try {
                            await updateFile(row, metadataSchema);
                            successCount++;
                            safeSetStatus(`Updated ${successCount}/${parsedRows.length} files...`);
                        } catch (error) {
                            console.error(`Error updating file ${row.fileId}:`, error);
                            failureCount++;
                            // Store the original row data from results
                            const originalRow = results.data.find(
                                (r: Record<string, string>) => r.fileId === row.fileId
                            );
                            if (originalRow) {
                                failedRowsData.push(originalRow);
                            }
                        }
                    }

                    setFailedRows(failedRowsData);
                    safeSetStatus(`Update complete. Successfully updated ${successCount} files. Failed: ${failureCount}`);
                    // Reset file input and state
                    setCsvFile(null);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                } catch (error) {
                    safeSetStatus(`Failed to initialize: ${error instanceof Error ? error.message : 'Unknown error'}`);
                }
            },
            error: (error) => {
                safeSetStatus(`Error parsing CSV: ${error.message}`);
            }
        });
    };

    const downloadSampleCSV = async () => {
        try {
            const metadataFields = await fetchMetadataFields();
            
            // Create headers: fileId, tags, and metadata fields
            const headers = [
                'fileId',
                'tags',
                ...metadataFields.map(field => `custom_metadata_${field.name}`)
            ];

            // Create sample row with empty values
            const sampleRow = headers.map(() => '');

            // Create CSV content
            const csvContent = [
                headers.join(','),
                sampleRow.join(',')
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'bulk_update_template.csv';
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            safeSetStatus(`Failed to generate sample CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    return (
        <Box>
            <VStack spacing={4} align="stretch">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <ChakraText fontSize="lg">Bulk Update Files</ChakraText>
                    <Button
                        variant="link"
                        color="blue.500"
                        onClick={downloadSampleCSV}
                        size="sm"
                    >
                        Download Sample Template
                    </Button>
                </Box>
                <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                />
                <Button onClick={handleParseCSV}>
                    Start bulk update
                </Button>
                {status && (
                    <ChakraText color="gray.600" fontSize="sm">
                        {status}
                    </ChakraText>
                )}
                {failedRows.length > 0 && (
                    <Button variant="link" color="red.500" onClick={downloadFailedRows}>
                        Download failed updates as CSV
                    </Button>
                )}
            </VStack>
        </Box>
    );
};
